// Edge auto-scroll for clip drag/resize gestures, both axes. While the
// pointer sits inside the activation zone at an edge of the visible tracks
// area, the container keeps scrolling and `onStep` re-runs the gesture's move
// logic so the drag follows even though no pointermove fires. Drag math must
// add `scrollDelta()` to its clientX delta and `scrollDeltaY()` to any
// hit-testing done against rects cached at gesture start — the container
// moving under a stationary pointer is still movement.
//
// `update(clientX, clientY)`: pass only the axes the gesture wants scrolled —
// resize handles pass just X, the row grip passes just Y (as undefined/omitted
// axis), a clip body drag passes both.

const EDGE_ZONE = 56; // px from the edge where horizontal auto-scroll kicks in
const MAX_STEP = 24; // px per animation frame at full depth
const V_EDGE_ZONE = 40; // vertical zone — rows are short, keep it tighter
const MAX_STEP_Y = 14;
const LABEL_WIDTH_FALLBACK = 150;
const RULER_HEIGHT_FALLBACK = 28;

export function createEdgeAutoScroll(fromEl, onStep) {
  const area = fromEl.closest(".it-tracks-area");
  if (!area) {
    return { update() {}, stop() {}, scrollDelta: () => 0, scrollDeltaY: () => 0 };
  }

  const startScrollLeft = area.scrollLeft;
  const startScrollTop = area.scrollTop;
  const labelWidth = area.querySelector(".it-label-corner")?.offsetWidth || LABEL_WIDTH_FALLBACK;
  const rulerHeight = area.querySelector(".it-ruler-row")?.offsetHeight || RULER_HEIGHT_FALLBACK;

  let vx = 0;
  let vy = 0;
  let raf = null;

  const tick = () => {
    raf = null;
    if (vx === 0 && vy === 0) return;
    const beforeX = area.scrollLeft;
    const beforeY = area.scrollTop;
    area.scrollLeft += vx;
    area.scrollTop += vy;
    if (area.scrollLeft !== beforeX || area.scrollTop !== beforeY) onStep();
    raf = requestAnimationFrame(tick);
  };

  return {
    // Call on every pointermove with the current pointer position.
    update(clientX, clientY) {
      const rect = area.getBoundingClientRect();

      if (typeof clientX === "number") {
        // Sticky labels cover the left side; clientWidth excludes the scrollbar.
        const leftBound = rect.left + labelWidth;
        const rightBound = rect.left + area.clientWidth;
        if (clientX > rightBound - EDGE_ZONE) {
          const depth = Math.min(1, (clientX - (rightBound - EDGE_ZONE)) / EDGE_ZONE);
          vx = Math.ceil(depth * MAX_STEP);
        } else if (clientX < leftBound + EDGE_ZONE) {
          const depth = Math.min(1, (leftBound + EDGE_ZONE - clientX) / EDGE_ZONE);
          vx = -Math.ceil(depth * MAX_STEP);
        } else {
          vx = 0;
        }
      } else {
        vx = 0;
      }

      if (typeof clientY === "number") {
        // The sticky ruler covers the top strip of the area.
        const topBound = rect.top + rulerHeight;
        const bottomBound = rect.top + area.clientHeight;
        if (clientY > bottomBound - V_EDGE_ZONE) {
          const depth = Math.min(1, (clientY - (bottomBound - V_EDGE_ZONE)) / V_EDGE_ZONE);
          vy = Math.ceil(depth * MAX_STEP_Y);
        } else if (clientY < topBound + V_EDGE_ZONE) {
          const depth = Math.min(1, (topBound + V_EDGE_ZONE - clientY) / V_EDGE_ZONE);
          vy = -Math.ceil(depth * MAX_STEP_Y);
        } else {
          vy = 0;
        }
      } else {
        vy = 0;
      }

      if ((vx !== 0 || vy !== 0) && raf === null) raf = requestAnimationFrame(tick);
    },
    stop() {
      vx = 0;
      vy = 0;
      if (raf !== null) {
        cancelAnimationFrame(raf);
        raf = null;
      }
    },
    scrollDelta: () => area.scrollLeft - startScrollLeft,
    scrollDeltaY: () => area.scrollTop - startScrollTop,
  };
}
